import {
  type GetActivitiesByLeadUuidResponse,
  type LeadActivityType,
} from '@app/queries/activities/getActivitiesByLeadUuid';
import activityService, { type ListActivitiesProps } from '@app/services/activities/activityService';
import { ACTIVITIES_TYPES, type ActivitiesType, NUM_ACTIVITIES_TO_FETCH } from '@data/activities/activities';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type ComparatorType } from '@repo/advanced-filters';
import { GLOBAL_TAG_TYPES } from '@repo/services';
import { message } from '@repo/ui';
import { getRelativeTimeKey, translate, TranslationEntity } from '@repo/utils';
import { type AppDispatch, type RootState } from '@store/index';
import { AsyncUtils } from '@vectord/utils';

export interface TimeFilterOptionsType {
  filterType: ComparatorType;
  offsetValue?: string;
  fromTime?: string;
  toTime?: string;
}

export interface ActivitiesTabState {
  activityTypeFilter: ActivitiesType;
  activityFilter?: {
    tagIds?: string[];
    activityIds: string[];
    allSelected?: boolean;
  },
  timeFilter?: TimeFilterOptionsType;
  loadedActivities: Record<string, LeadActivityType[]>;
  lastLoadedActivityUuid?: string;
  // This is used to store the uuid of the lead for which activities are being fetched
  activitiesForUuid?: string;
  paginationConfig: {
    hasNextPage: boolean;
    endCursor?: string;
  };
  // newActivityLoading: boolean;
  newActivityLoaderConfig: {
    isLoading: boolean;
    createdActivityUuid?: string;
  }
}

const initialState: ActivitiesTabState = {
  activityTypeFilter: ACTIVITIES_TYPES.all,
  activityFilter: undefined,
  timeFilter: undefined,
  loadedActivities: {},
  lastLoadedActivityUuid: undefined,
  activitiesForUuid: undefined,
  paginationConfig: {
    hasNextPage: true,
  },
  newActivityLoaderConfig: {
    isLoading: false,
  },
};

export const REFRESH_ACTIVITIES_DELAY = 3000;
const MAX_RETRY_COUNT = 3;
const LOADING_FALLBACK_MESSAGE = translate(
  `Good things take time! Refresh in a few seconds to see your #{${TranslationEntity.activity}}`,
  [{ capitalize: true }],
);

export const activitiesTabSlice = createSlice({
  name: 'activitiesTab',
  initialState,
  reducers: {
    changeActivityTypeFilter: (state, action: PayloadAction<ActivitiesType>) => {
      state.activityTypeFilter = action.payload;
    },
    resetLoadedActivities: (state) => {
      state.loadedActivities = initialState.loadedActivities;
      state.lastLoadedActivityUuid = initialState.lastLoadedActivityUuid;
      state.paginationConfig = initialState.paginationConfig;
      state.activitiesForUuid = undefined;
      state.newActivityLoaderConfig = initialState.newActivityLoaderConfig;
    },
    setActivityFilter: (state, action: PayloadAction<ActivitiesTabState['activityFilter']>) => {
      state.activityFilter = action.payload;
      activitiesTabSlice.caseReducers.resetLoadedActivities(state);
    },
    modifyTimeFilter: (state, action: PayloadAction<TimeFilterOptionsType>) => {
      state.timeFilter = action.payload;
    },
    setPaginationConfig: (state, action: PayloadAction<ActivitiesTabState['paginationConfig']>) => {
      state.paginationConfig = action.payload;
    },
    validateAndResetInvalidData: (state, action: PayloadAction<string>) => {
      if (state.activitiesForUuid !== action.payload) {
        activitiesTabSlice.caseReducers.resetLoadedActivities(state);
      }
    },
    addLoadedActivities: (state, action: PayloadAction<{
      activity: GetActivitiesByLeadUuidResponse['leadActivities'],
      forUuid: string
    }>) => {
      if (state.activitiesForUuid !== action.payload.forUuid) {
        activitiesTabSlice.caseReducers.resetLoadedActivities(state);
      }
      state.paginationConfig = action.payload.activity.pageInfo;
      const activities = action.payload.activity.leadActivities;
      activities.forEach((activity) => {
        // Get the time Key Like Today, Yesterday, Previous 7 days
        const activityTimeKey = getRelativeTimeKey(activity.activityTime);
        const activitiesForTime = state.loadedActivities[activityTimeKey] ?? [];
        if (!activitiesForTime.find((act) => act.leadActivityUuid === activity.leadActivityUuid)) {
          activitiesForTime.push(activity);
        }
        state.loadedActivities[activityTimeKey] = activitiesForTime;
      });

      state.lastLoadedActivityUuid = activities[activities.length - 1]?.leadActivityUuid;
      state.activitiesForUuid = action.payload.forUuid;
    },
    deleteActivity: (state, action: PayloadAction<string>) => {
      const activityUuid = action.payload;
      Object.keys(state.loadedActivities).forEach((key) => {
        state.loadedActivities[key] = state.loadedActivities[key]?.filter(
          (activity) => activity.leadActivityUuid !== activityUuid,
        ) ?? [];
      });
    },
    setNewActivityLoadingState: (state, action: PayloadAction<{
      isLoading: boolean;
      createdActivityUuid?: string;
    }>) => {
      state.newActivityLoaderConfig.isLoading = action.payload.isLoading;
      state.newActivityLoaderConfig.createdActivityUuid = action.payload.createdActivityUuid;
    },
  },
});

export const {
  changeActivityTypeFilter,
  modifyTimeFilter,
  resetLoadedActivities,
  setActivityFilter,
  setPaginationConfig,
  addLoadedActivities,
  deleteActivity,
  validateAndResetInvalidData,
  setNewActivityLoadingState,
} = activitiesTabSlice.actions;

export const resetLoadedActivitiesThunk = () => (dispatch: AppDispatch) => {
  dispatch(resetLoadedActivities());
  dispatch(activityService.util.invalidateTags([GLOBAL_TAG_TYPES.leadActivityList]));
};

export const loadAllActivitiesInPlaceThunk = (leadUuid: string, retryCount = 0) => async (
  dispatch: AppDispatch,
  getState: () => RootState,
) => {
  const state = getState();
  const currentState = state.leadDetail.activitiesTab;
  const loaderConfing = currentState.newActivityLoaderConfig;

  // step 1: Invalidate the related rtk query
  dispatch(activityService.util.invalidateTags([GLOBAL_TAG_TYPES.leadActivityList]));
  // step 2: Build the related query params
  const queryParams: ListActivitiesProps = {
    leadUuid,
    limit: NUM_ACTIVITIES_TO_FETCH,
    activityIds: currentState.activityFilter?.activityIds.map((activity) => parseInt(activity, 10)) ?? [],
    activitySubtypes: currentState.activityFilter?.tagIds,
    timeFilter: currentState.timeFilter,
  };

  const response = await dispatch(activityService.endpoints.listActivities.initiate(queryParams));

  if (response.data && response.data?.leadActivities) {
    // check if the new activity is created.
    const loadedActivityUuids = response.data?.leadActivities?.leadActivities?.map(
      (activity) => activity.leadActivityUuid,
    );
    if (loaderConfing.createdActivityUuid && loadedActivityUuids.includes(loaderConfing.createdActivityUuid)) {
      dispatch(resetLoadedActivities());
      dispatch(addLoadedActivities({
        activity: response.data?.leadActivities,
        forUuid: leadUuid,
      }));
      message.success(translate(`#{${TranslationEntity.activity}} created successfully`, [{
        capitalize: true,
      }]));
    } else if (retryCount < MAX_RETRY_COUNT) {
      AsyncUtils.waitAndExecute(() => {
        dispatch(loadAllActivitiesInPlaceThunk(leadUuid, retryCount + 1));
      }, REFRESH_ACTIVITIES_DELAY);
      return;
    } else {
      // after trying for MAX_RETRY_COUNT times, show the fallback message
      message.info(LOADING_FALLBACK_MESSAGE);
    }
  } else {
    // on API Error
    message.info(LOADING_FALLBACK_MESSAGE);
  }
  dispatch(setNewActivityLoadingState({ isLoading: false, createdActivityUuid: undefined }));
};

export default activitiesTabSlice.reducer;
